import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
// eslint-disable-next-line lodash/import-scope
import property from 'lodash/property';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import { Box, Title, Text, Link, Accent } from '@nerdwallet/currency';
import Image from '@nerdwallet/react-image';

import BackgroundColor from '~/client/components/background-color/background-color';
import getTextColor from '../../lib/text-color-config';
import {
  boolCheckboxSpec,
  repeatable,
  stringInputSpec,
  stringSelectSpec,
  stringTextAreaSpec,
  anchorIdInput,
} from '../../lib/editing-configs';
import styles from './branded-article-calculator.module.less';
import './coin-animations.less';
import Calculator from '../calculator/calculator';
import useOnScreen from './lib/useOnScreen';
import coinImagesUrls from './lib/coinImagesUrls';

const getTextColorStyle = (textColorOptions, propertyName = 'color') => {
  const { textColor: color } = getTextColor(textColorOptions);
  return { [propertyName]: color };
};

const BrandedArticleCalculator = ({
  anchorId,
  bgOptions,
  sectionIconUrl,
  sectionIconUrlMobile,
  sectionIconPosition,
  // header section
  preheader,
  preheaderColorOptions,
  title,
  titleSize,
  titleChronicle,
  titleColorOptions,
  subtitle,
  subtitleSize,
  subtitleColorOptions,
  // Article section
  articleImageUrl,
  articleImageUrlMobile,
  cffId,
  fullWidthMobile,
  articles,
  includeCoinsAnimation,
  bottomBorderHideForMobile,
  size,
}) => {
  const containerRef = useRef();
  const isSectionVisible = useOnScreen(containerRef);
  const coinContainerRef = useRef();
  const sectionIconRef = useRef();

  const getSectionIcon = () => {
    if (sectionIconUrlMobile && size !== 'lg') {
      return sectionIconUrlMobile;
    }
    if (sectionIconUrl) {
      return sectionIconUrl;
    }
    return null;
  };

  const getArticleImage = () => {
    if (articleImageUrlMobile && size !== 'lg') {
      return articleImageUrlMobile;
    }
    if (articleImageUrl) {
      return articleImageUrl;
    }
    return null;
  };

  const handleOnScroll = useCallback(() => {
    if (isSectionVisible && containerRef.current && sectionIconRef.current) {
      sectionIconRef.current.classList.add('coinAnimation');
    }

    if (includeCoinsAnimation && coinContainerRef.current && isSectionVisible) {
      coinContainerRef.current.childNodes.forEach((element) => {
        // add 'coinAnimation' for those elements who reaches the half screen
        if (element.getBoundingClientRect().y <= window.innerHeight / 2) {
          element.classList.add('coinAnimation');
        }
      });
    }
  }, [includeCoinsAnimation, isSectionVisible]);

  useEffect(() => {
    window.addEventListener('scroll', handleOnScroll);
    return () => window.removeEventListener('scroll', handleOnScroll);
  }, [handleOnScroll]);

  return (
    <BackgroundColor bgOptions={bgOptions}>
      {(backgroundColor) => (
        <div
          id={anchorId}
          ref={containerRef}
          className={styles.container}
          style={{ backgroundColor }}
        >
          {getSectionIcon() && (
            <Box
              ref={sectionIconRef}
              style={{ [sectionIconPosition]: size === 'lg' ? '13%' : 0 }}
              className={classNames([styles.sectionIconBox])}
            >
              <Image
                naturalHeight={161}
                naturalWidth={161}
                backgroundColor="transparent"
                src={getSectionIcon()}
                alt="section Image"
              />
            </Box>
          )}
          <Box className={styles.headerSection} id="header-section">
            <Box
              className={styles.preheaderBox}
              style={getTextColorStyle(preheaderColorOptions)}
            >
              <Accent id="preheader">{preheader}</Accent>
            </Box>

            <Title
              size={titleSize}
              style={getTextColorStyle(titleColorOptions)}
              className={classNames([
                styles.title,
                {
                  [styles.inChronicle]: titleChronicle,
                },
              ])}
              id="title"
            >
              {title}
            </Title>

            <Box style={getTextColorStyle(subtitleColorOptions)}>
              <Text size={subtitleSize} id="subtitle">
                {subtitle}
              </Text>
            </Box>
          </Box>
          <Box className={styles.content}>
            <Box className={styles.articleSection}>
              {getArticleImage() && (
                <Image
                  naturalHeight={334}
                  naturalWidth={328}
                  backgroundColor="transparent"
                  className={styles.articleImage}
                  src={getArticleImage()}
                  alt="article Image"
                />
              )}
              <Box className={styles.articles}>
                {articles.map((articleItem) => (
                  <Box
                    key={articleItem.$unique_key}
                    className={styles.articles}
                  >
                    <Box
                      style={getTextColorStyle(
                        articleItem.articleTitleColorOptions
                      )}
                    >
                      <Text bold size="large">
                        {articleItem.articleTitle}
                      </Text>
                    </Box>
                    <Box
                      className={styles.articleTextBox}
                      style={getTextColorStyle(
                        articleItem.articleTextColorOptions
                      )}
                    >
                      <Text>{articleItem.articleText}</Text>
                    </Box>
                    <Box
                      className={styles.articleLinkBox}
                      style={getTextColorStyle(
                        articleItem.articleLinkColorOptions
                      )}
                    >
                      <Link href={articleItem.articleLinkUrl}>
                        {articleItem.articleLinkText}
                      </Link>
                    </Box>
                    {articleItem.displayBottomBorder && (
                      <Box
                        style={{
                          visibility:
                            size !== 'lg' && bottomBorderHideForMobile
                              ? 'hidden'
                              : 'visible',
                          width: '100%',
                          height: articleItem.bottomBorderPixels || 1,
                          ...getTextColorStyle(
                            articleItem.articleLinkColorOptions,
                            'backgroundColor'
                          ),
                        }}
                      />
                    )}
                  </Box>
                ))}

                {includeCoinsAnimation && (
                  <Box className={styles.coinsContainer} ref={coinContainerRef}>
                    <Image
                      naturalHeight={100}
                      naturalWidth={100}
                      backgroundColor="transparent"
                      className={styles.firstCoinImage}
                      src={coinImagesUrls[0]}
                      alt="coin Image"
                    />
                    <Image
                      naturalHeight={100}
                      naturalWidth={100}
                      backgroundColor="transparent"
                      className={styles.secondCoinImage}
                      src={coinImagesUrls[1]}
                      alt="coin Image"
                    />
                    <Image
                      naturalHeight={100}
                      naturalWidth={100}
                      backgroundColor="transparent"
                      className={styles.thirdCoinImage}
                      src={coinImagesUrls[2]}
                      alt="coin Image"
                    />
                    <Image
                      naturalHeight={100}
                      naturalWidth={100}
                      backgroundColor="transparent"
                      className={styles.firstBurstImage}
                      src={coinImagesUrls[3]}
                      alt="coin Image"
                    />
                    <Image
                      naturalHeight={100}
                      naturalWidth={100}
                      backgroundColor="transparent"
                      className={styles.forthCoinImage}
                      src={coinImagesUrls[0]}
                      alt="coin Image"
                    />
                    <Image
                      naturalHeight={100}
                      naturalWidth={100}
                      backgroundColor="transparent"
                      className={styles.secondBurstImage}
                      src={coinImagesUrls[3]}
                      alt="coin Image"
                    />
                  </Box>
                )}
              </Box>
            </Box>
            <Box className={styles.calculatorSection}>
              <Calculator cffId={cffId} fullWidthMobile={fullWidthMobile} />
            </Box>
          </Box>
        </div>
      )}
    </BackgroundColor>
  );
};

BrandedArticleCalculator.displayName = 'Branded Article Calculator';

BrandedArticleCalculator.propTypes = {
  anchorId: PropTypes.string,
  sectionIconUrl: PropTypes.string,
  sectionIconUrlMobile: PropTypes.string,
  sectionIconPosition: PropTypes.string,
  // header section
  preheader: PropTypes.string,
  title: PropTypes.string,
  titleSize: PropTypes.oneOf(['default', 'small']),
  subtitle: PropTypes.string,
  subtitleSize: PropTypes.oneOf(['default', 'large', 'small', 'x-small']),
  bgOptions: BackgroundColor.propTypes.bgOptions,
  // Article section
  articleImageUrl: PropTypes.string,
  articleImageUrlMobile: PropTypes.string,
  articles: PropTypes.array,
  includeCoinsAnimation: PropTypes.bool,
  bottomBorderHideForMobile: PropTypes.bool,
  ...Calculator.propTypes,
};

BrandedArticleCalculator.defaultProps = {
  anchorId: '',
  sectionIconUrl: '',
  sectionIconUrlMobile: '',
  sectionIconPosition: 'left',
  // header section
  preheader: '',
  title: '',
  titleSize: 'small',
  subtitle: '',
  subtitleSize: 'large',
  // Article section
  articleImageUrl: '',
  articleImageUrlMobile: '',
  articles: [],
  includeCoinsAnimation: false,
  bottomBorderHideForMobile: false,
  ...Calculator.defaultProps,
};

BrandedArticleCalculator.getEditingConfig = (componentDocument) => [
  anchorIdInput,
  ...BackgroundColor.getEditingConfig(componentDocument),
  stringInputSpec('sectionIconUrl', 'Section Icon URL'),
  stringInputSpec('sectionIconUrlMobile', 'Section Icon URL Mobile'),
  stringSelectSpec(
    `sectionIconPosition`,
    'Section Icon Position',
    [
      { label: 'Left', value: 'left' },
      { label: 'Right', value: 'right' },
    ],
    {
      help: 'Defaults to left',
    }
  ),
  // header section
  stringInputSpec('preheader', 'Preheader'),
  ...getTextColor.getEditingConfig(
    componentDocument,
    'preheaderColorOptions',
    'Preheader Color'
  ),
  stringInputSpec('title', 'Title'),
  stringSelectSpec(
    `titleSize`,
    'Title Size',
    [
      { label: 'Default', value: 'default' },
      { label: 'Small', value: 'small' },
    ],
    {
      help: 'Defaults to small',
    }
  ),
  boolCheckboxSpec('titleChronicle', 'Title in Chronicle?'),
  ...getTextColor.getEditingConfig(
    componentDocument,
    'titleColorOptions',
    'Title Color'
  ),
  stringInputSpec('subtitle', 'Subtitle'),
  stringSelectSpec(
    `subtitleSize`,
    'Subtitle Size',
    [
      { label: 'Default', value: 'default' },
      { label: 'Large', value: 'large' },
      { label: 'Small', value: 'small' },
      { label: 'X-Small', value: 'x-small' },
    ],
    {
      help: 'Defaults to large',
    }
  ),
  ...getTextColor.getEditingConfig(
    componentDocument,
    'subtitleColorOptions',
    'Subtitle Color'
  ),
  // Article section
  stringInputSpec('articleImageUrl', 'Article Image Url'),
  stringInputSpec('articleImageUrlMobile', 'Article Image Url Mobile'),
  repeatable('articles', 'Articles', 'Article', (i) => [
    stringInputSpec('articleTitle', 'Article Title'),
    ...getTextColor.getEditingConfig(
      componentDocument,
      'articleTitleColorOptions',
      'Article Title Color',
      'Article Title Hex color override',
      `articles[${i}].articleTitleColorOptions`
    ),
    stringTextAreaSpec('articleText', 'Article Text'),
    ...getTextColor.getEditingConfig(
      componentDocument,
      'articleTextColorOptions',
      'Article Text Color',
      'Article Text Hex color override',
      `articles[${i}].articleTextColorOptions`
    ),
    stringInputSpec('articleLinkText', 'Article Link Text'),
    stringInputSpec('articleLinkUrl', 'Article Link URL'),
    ...getTextColor.getEditingConfig(
      componentDocument,
      'articleLinkColorOptions',
      'Article Link Color',
      'Article Link Hex color override',
      `articles[${i}].articleLinkColorOptions`
    ),
    boolCheckboxSpec('displayBottomBorder', 'Display Bottom Border?'),
    stringInputSpec('bottomBorderPixels', 'Bottom Border Pixels', {
      help: 'Defaults to 1px',
    }),
  ]),
  boolCheckboxSpec('includeCoinsAnimation', 'Include Coins Animation?'),
  boolCheckboxSpec(
    'bottomBorderHideForMobile',
    'Bottom Border Hide For Mobile?'
  ),
  ...Calculator.getEditingConfig(),
];

export default connect(
  createStructuredSelector({
    size: property('mediaQuery'),
  })
)(BrandedArticleCalculator);
